/**
 * Das bisschen Markdown, das eine **Notiz** versteht — Absatz, Liste,
 * Umbruch. Siehe „Notiz (Canvas)" in CONTEXT.md.
 *
 * **Genau drei Formen, weil genau drei gebraucht werden.** Kein Fett, kein
 * Kursiv, keine Links, keine Überschriften: Sie stehen nicht im Entwurf, und
 * jede davon zöge einen Formatierungs-Balken nach sich — den es hier bewusst
 * nicht gibt.
 *
 * **Im Bearbeiten-Modus sieht man die Rohform** (`- Team Meeting`), außerhalb
 * die gesetzte (`• Team Meeting`). Das ist die ehrlichste Lösung ohne Editor:
 * Was man tippt, ist das, was gespeichert wird — und der Text bleibt für den
 * Sync, das **Rückgängig** und die **Übernahme** in Aufgaben ein schlichter
 * String.
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`.
 */

/** Woran eine Listenzeile erkannt wird. */
const LIST_MARKER = /^- ?/

export type MarkdownBlock =
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'list'; items: string[] }

/**
 * Der Text als Folge von Blöcken.
 *
 * Absätze trennt die Leerzeile, Listen der führende Strich. Eine Liste endet
 * bei der ersten Zeile ohne Strich — ohne dass eine Leerzeile nötig wäre: Wer
 * nach der Liste weiterschreibt, meint einen neuen Absatz.
 */
export function parseNoteMarkdown(content: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = []
  let paragraph: string[] = []
  let list: string[] | null = null

  const flush = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'paragraph', lines: paragraph })
      paragraph = []
    }
    if (list !== null) {
      blocks.push({ kind: 'list', items: list })
      list = null
    }
  }

  for (const line of content.split('\n')) {
    if (line.trim() === '') {
      // Die Leerzeile schließt ab, was offen ist — und erzeugt selbst nichts.
      flush()
      continue
    }

    if (LIST_MARKER.test(line)) {
      // Ein angefangener Absatz endet hier, ohne dass eine Leerzeile nötig war.
      if (paragraph.length > 0) flush()
      list ??= []
      list.push(line.replace(LIST_MARKER, ''))
      continue
    }

    if (list !== null) flush()
    paragraph.push(line)
  }

  flush()
  return blocks
}

/**
 * Enter in einer Liste — die einzige Schreibhilfe, die es gibt.
 *
 * Sie setzt den nächsten Strich; ein zweites Enter auf einem leeren Punkt
 * beendet die Liste. Ohne das erste müsste man jeden Strich selbst tippen,
 * ohne das zweite käme man aus einer Liste nur heraus, indem man ihn löscht.
 *
 * @param caret die Stelle des Carets im Text
 * @returns der neue Text samt neuer Zeigerstelle — oder `null`, wenn Enter
 *   hier nichts Besonderes bedeutet und der Aufrufer es durchlassen soll
 */
export function continueListOnEnter(
  content: string,
  caret: number,
): { content: string; caret: number } | null {
  const before = content.slice(0, caret)
  const lineStart = before.lastIndexOf('\n') + 1
  const line = before.slice(lineStart)

  if (!LIST_MARKER.test(line)) return null

  // Ein leerer Punkt: die Liste endet, der Strich verschwindet.
  if (line.replace(LIST_MARKER, '').trim() === '') {
    return {
      content: content.slice(0, lineStart) + content.slice(caret),
      caret: lineStart,
    }
  }

  return {
    content: `${before}\n- ${content.slice(caret)}`,
    caret: caret + 3,
  }
}
